import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Form, Formik } from "formik";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { enums } from "../../constants/enum";
import usePurchaseService from "../../services/api/purchaseService";
import dateFormatter from "../../util/dateFormatter";
import currencyFormatter from "../../util/currencyFormatter";

import {
  AddIcon,
  ArrowLongRightIcon,
  CloseIcon,
  SearchIcon,
} from "../../assets/icons";
import { Button, Loading, PageTitle, Section } from "../../components";
import { DateInput, TextInput } from "../../components/forms";
import "./PurchasesPage.scss";

const validationSchema = Yup.object().shape({
  from: Yup.date().nullable(),
  to: Yup.date()
    .nullable()
    .min(Yup.ref("from"), "To date must be after From date"),
});

function PurchasesPage() {
  const { ref, inView } = useInView();
  const { getPurchases, queryKey } = usePurchaseService();
  const [filters, setFilters] = useState({
    purchaseNumber: "",
    from: "",
    to: "",
  });

  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [queryKey, filters],
      queryFn: ({ pageParam = 1 }) =>
        getPurchases(
          filters.purchaseNumber,
          filters.from,
          filters.to,
          pageParam,
        ),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
    });

  const handleSearch = useCallback((values) => {
    setFilters(values);
  }, []);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  return (
    <div className="page purchases-page">
      <PageTitle text="Purchases">
        <Link to="/purchases/add">
          <Button text="New Purchase" icon={<AddIcon />} />
        </Link>
      </PageTitle>

      <Section title="Search">
        <Formik
          initialValues={filters}
          validationSchema={validationSchema}
          onSubmit={handleSearch}
          enableReinitialize
          onReset={() => setFilters({ purchaseNumber: "", from: "", to: "" })}
        >
          <Form>
            <TextInput
              name="purchaseNumber"
              label="Search by Purchase Number"
              placeholder="Enter Bill number"
            />

            <div className="input-group">
              <DateInput name="from" label="From" />
              <DateInput name="to" label="To" />
            </div>

            <div className="form-actions">
              <Button text="Search" icon={<SearchIcon />} type="submit" />
              <Button
                text="Clear"
                icon={<CloseIcon />}
                type="reset"
                outlined
                className="clear-btn"
              />
            </div>
          </Form>
        </Formik>

        <div className="search-results">
          <div className="purchase-list">
            <div className="table-title">
              <h3>Search Results</h3>
            </div>

            <div className="table-content">
              <table>
                <thead>
                  <tr>
                    <th>Purchase Date</th>
                    <th>Amount</th>
                    <th>Bill Number</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {data?.pages?.map((page) =>
                    page.purchases?.map((purchase) => (
                      <tr key={purchase._id}>
                        <td>{dateFormatter(purchase.createdAt)}</td>
                        <td>
                          {purchase.totalPrice
                            ? `${currencyFormatter(purchase.totalPrice)} LKR`
                            : "-"}
                        </td>
                        <td>{purchase.billNumber || "-"}</td>
                        <td>
                          <Link to={`/purchases/details/${purchase._id}`}>
                            <Button
                              text="View"
                              prefixIcon={<ArrowLongRightIcon />}
                            />
                          </Link>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px 0",
                }}
                ref={ref}
              >
                {isFetchingNextPage ? <Loading variant="inline" /> : null}
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default PurchasesPage;
